import { BlockCtaButtons } from "@/components/block-cta-buttons";
import { Band, Eyebrow, displayFont } from "./band-helpers";
import type { WeeklyHighlightsBlock } from "@/lib/storefront-types";

type WeeklyHighlightsSectionProps = {
  block: WeeklyHighlightsBlock;
};

export function WeeklyHighlightsSection({
  block,
}: WeeklyHighlightsSectionProps) {
  const isLight = block.appearance === "light";
  const isFrozen = block.appearance === "frozen";
  const isDark = !isLight;

  return (
    <Band
      className={
        isLight
          ? "border-y storefront-border-soft bg-[#fbfdf9] py-12 sm:py-14"
          : isFrozen
            ? "storefront-surface-frozen py-12 sm:py-14"
            : "storefront-surface-dark py-12 sm:py-14"
      }
      aria-label="This week's schedule"
    >
      <div className="grid gap-10 lg:grid-cols-[1.02fr_0.98fr] lg:items-start">
        <div className="space-y-5">
          {block.eyebrow ? (
            <Eyebrow inverse={isDark}>{block.eyebrow}</Eyebrow>
          ) : null}
          <h2
            className={`${displayFont.className} max-w-2xl text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight ${
              isDark ? "text-white" : "storefront-text-heading"
            }`}
          >
            {block.headline}
          </h2>
          <div
            className={`max-w-xl space-y-4 text-base leading-7 sm:text-lg ${
              isDark ? "text-white/72" : "storefront-text-body"
            }`}
            dangerouslySetInnerHTML={{ __html: block.bodyHtml }}
          />
          <BlockCtaButtons buttons={block.buttons} />
        </div>

        <div className="grid gap-3">
          {block.highlightItems.map((item) => (
            <article
              key={`${item.label}-${item.detail}`}
              className={`rounded-[1.6rem] px-5 py-5 ${
                isLight
                  ? "border storefront-border-soft bg-white storefront-text-heading shadow-[0_20px_44px_-34px_rgba(0,39,0,0.2)]"
                  : isFrozen
                    ? "storefront-border-primary-soft storefront-surface-frozen-soft border text-white backdrop-blur-sm"
                    : "border border-white/10 bg-white/6 text-white backdrop-blur-sm"
              }`}
            >
              <p
                className={`text-[0.7rem] font-semibold uppercase tracking-[0.26em] ${
                  isLight ? "storefront-text-primary" : "storefront-text-primary-tint"
                }`}
              >
                {item.label}
              </p>
              <p
                className={`mt-2 text-sm leading-6 ${
                  isLight ? "storefront-text-body" : isFrozen ? "text-white/84" : "text-white/82"
                }`}
              >
                {item.detail}
              </p>
            </article>
          ))}
        </div>
      </div>
    </Band>
  );
}
